import type { BaseComponentProps } from "@neru/types";
import { useEffect, useState } from "react";

import { Icon } from "../Icon/Icon";

export interface CheckboxProps extends BaseComponentProps {
  checked?: boolean;
  disabled?: boolean;
  label?: string;
  name?: string;
  onClick?: (e: React.SyntheticEvent<Element, Event>) => void | Promise<void>;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  value?: string;
}

export const Checkbox = ({
  checked = false,
  className = "",
  "data-testid": testId = "checkbox",
  disabled = false,
  label,
  name,
  onClick,
  onKeyDown,
  value = "true",
}: CheckboxProps) => {
  const [isChecked, setIsChecked] = useState(checked);

  useEffect(() => {
    if (typeof checked === "boolean") {
      setIsChecked(checked);
    }
  }, [checked]);

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const checkboxStyles = [
    disabled
      ? `disabled:cursor-not-allowed
        disabled:border-primary-300
        disabled:bg-primary-100
        dark:disabled:border-primary-600
        dark:disabled:bg-primary-800`
      : `hover:border-accent-check-faintLight
        checked:bg-accent-check-primary
        checked:border-accent-check-primary
        checked:group-hover:bg-accent-check-faintLight
        checked:group-hover:border-accent-check-faintLight
        dark:border-primary-400
        dark:group-hover:border-accent-check-faintDark
        dark:checked:bg-accent-check-text
        dark:checked:border-accent-check-text
        dark:checked:group-hover:border-accent-check-faintDark
        dark:checked:group-hover:bg-accent-check-faintDark`,
  ]
    .filter(Boolean)
    .join(" ");

  const iconStyles = [
    disabled
      ? "fill-primary-300 dark:fill-primary-600 cursor-not-allowed"
      : "fill-accent-check-text dark:fill-accent-check-primary",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <label
      className={`
        group text-primary-1000 flex cursor-pointer items-center
        dark:text-primary-0
        ${className}
      `}
    >
      <input
        checked={isChecked}
        className={`
          border-primary-500 bg-primary-0 h-6 w-6 cursor-pointer appearance-none
          rounded border-2
          dark:bg-fixed-primary-1000
          ${checkboxStyles}
          focus:outline-none
        `}
        data-testid={testId}
        disabled={disabled}
        name={name}
        onChange={handleCheckboxChange}
        onClick={onClick}
        onKeyDown={onKeyDown}
        type="checkbox"
        value={value}
      />
      {isChecked && (
        <Icon
          className={`
            absolute
            ${iconStyles}
            ml-1
          `}
          size="small"
          type="check-solid"
        />
      )}

      {label ? <span className="ml-2">{label}</span> : null}
    </label>
  );
};
